import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  LinearProgress,
  Grid,
  IconButton,
  Chip,
  InputAdornment,
  FormControlLabel,
  Switch,
  Alert,
  Menu,
  MenuItem,
  CardMedia,
  CardActions,
  Slider,
  Tabs,
  Tab
} from '@mui/material';
import {
  Add,
  EmojiEvents,
  AttachMoney,
  Delete,
  Star,
  Launch,
  MoreVert,
  PhotoCamera,
  ShoppingCart
} from '@mui/icons-material';
import { collection, addDoc, updateDoc, deleteDoc, doc, getDocs, query, where, orderBy } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase';
import { extractAmazonProduct } from '../utils/amazonHelper';
import { useAuth } from '../contexts/AuthContext';

function SavingsGoalsEnhanced({ userId, currentBalance, onContribute, familyId }) {
  const { currentUser } = useAuth();
  const [goals, setGoals] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [openContributeDialog, setOpenContributeDialog] = useState(false);
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [newGoal, setNewGoal] = useState({
    name: '',
    targetAmount: 0,
    amazonUrl: '',
    amazonImageUrl: '',
    autoAllocate: false,
    autoAllocatePercent: 10
  });
  const [contributeType, setContributeType] = useState(0); // 0=fixed, 1=percent
  const [contributeAmount, setContributeAmount] = useState(0);
  const [contributePercent, setContributePercent] = useState(10);
  const [loading, setLoading] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [confirmDeleteGoal, setConfirmDeleteGoal] = useState({ open: false, goalId: null });
  const [confirmRequestPurchase, setConfirmRequestPurchase] = useState({ open: false, goal: null });
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');

  useEffect(() => {
    loadGoals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  const loadGoals = async () => {
    if (!currentUser) {
      console.log('User not authenticated, skipping goal loading');
      return;
    }
    
    try {
      const goalsRef = collection(db, 'savingsGoals');
      const q = query(
        goalsRef,
        where('userId', '==', currentUser.uid),
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(q);
      const goalsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setGoals(goalsData);
    } catch (error) {
      console.error('Error loading goals:', error);
    }
  };

  const handleImageUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
    
    setImageFile(file);
    
    // Reset the input so the same file can be selected again
    event.target.value = '';
  };

  const uploadImageToStorage = async (file) => {
    if (!file) return null;

    try {
      const timestamp = Date.now();
      const storageRef = ref(storage, `goalImages/${userId}/${timestamp}_${file.name}`);
      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);
      return downloadURL;
    } catch (error) {
      console.error('Error uploading image:', error);
      throw error;
    }
  };

  const handleExtractAmazonProduct = async () => {
    if (!newGoal.amazonUrl) {
      alert('Please paste an Amazon URL first');
      return;
    }
    
    setExtracting(true);
    try {
      console.log('🛒 Starting Amazon extraction for:', newGoal.amazonUrl);
      const productData = await extractAmazonProduct(newGoal.amazonUrl);
      console.log('📦 Extraction successful:', productData);
      
      setNewGoal({
        ...newGoal,
        name: productData.name || newGoal.name,
        targetAmount: productData.price || newGoal.targetAmount,
        amazonImageUrl: productData.imageUrl // Store Amazon image separately
      });
      
      // Don't overwrite user's uploaded photo with Amazon image
      // Amazon images often have CORS issues and violate affiliate terms for child accounts
      
      alert(`Product extracted! Name: "${productData.name}", Price: $${productData.price}.\n\nYour uploaded photo will be used (Amazon images not shown to comply with affiliate terms).`);
    } catch (error) {
      console.error('❌ Error extracting Amazon product:', error);
      alert('Error extracting product: ' + error.message + '\n\nNote: This is a basic extraction. Please manually enter the product name and price.');
    } finally {
      setExtracting(false);
    }
  };

  const handleCreateGoal = async () => {
    if (!newGoal.name || newGoal.targetAmount <= 0) {
      alert('Please enter a goal name and target amount');
      return;
    }

    setLoading(true);
    setUploadingImage(true);
    
    try {
      let imageUrl = '';
      if (imageFile) {
        try {
          imageUrl = await uploadImageToStorage(imageFile);
        } catch (uploadErr) {
          console.warn('Image upload failed, creating goal without image:', uploadErr);
          // Continue without image — don't block goal creation
        }
      } else if (imagePreview) {
        imageUrl = imagePreview;
      }

      const goalData = {
        name: newGoal.name.trim(),
        targetAmount: parseFloat(newGoal.targetAmount),
        currentAmount: 0,
        imageUrl: imageUrl || '',
        amazonUrl: newGoal.amazonUrl || '',
        autoAllocate: newGoal.autoAllocate,
        autoAllocatePercent: newGoal.autoAllocate ? newGoal.autoAllocatePercent : 0,
        userId: currentUser.uid,
        familyId: familyId || null,
        createdAt: new Date().toISOString(),
        status: 'active' // active, funded, pending_approval, approved
      };

      await addDoc(collection(db, 'savingsGoals'), goalData);

      setOpenDialog(false);
      setNewGoal({
        name: '',
        targetAmount: 0,
        amazonUrl: '',
        amazonImageUrl: '',
        autoAllocate: false,
        autoAllocatePercent: 10
      });
      setImageFile(null);
      setImagePreview('');
      
      loadGoals();
    } catch (error) {
      console.error('Error creating goal:', error);
      alert(`Error creating goal: ${error.message}\n\nPlease try again.`);
    } finally {
      setLoading(false);
      setUploadingImage(false);
    }
  };

  const handleUpdateGoal = async (goalId, updates) => {
    try {
      await updateDoc(doc(db, 'savingsGoals', goalId), updates);
      loadGoals();
    } catch (error) {
      console.error('Error updating goal:', error);
      alert('Error updating goal: ' + error.message);
    }
  };

  const handleDeleteGoal = (goalId) => {
    setConfirmDeleteGoal({ open: true, goalId });
    handleCloseMenu();
  };

  const executeDeleteGoal = async (goalId) => {
    try {
      await deleteDoc(doc(db, 'savingsGoals', goalId));
      loadGoals();
    } catch (error) {
      console.error('Error deleting goal:', error);
    }
  };

  const handleContribute = async () => {
    let amount = contributeAmount;
    
    if (contributeType === 1) {
      amount = (currentBalance * contributePercent) / 100;
    }
    
    if (amount <= 0) {
      alert('Please enter an amount');
      return;
    }
    
    if (amount > currentBalance) {
      alert(`You only have $${currentBalance.toFixed(2)} available`);
      return;
    }

    try {
      const newAmount = Math.min(selectedGoal.currentAmount + amount, selectedGoal.targetAmount);
      const newStatus = newAmount >= selectedGoal.targetAmount ? 'funded' : 'active';
      
      await handleUpdateGoal(selectedGoal.id, {
        currentAmount: newAmount,
        lastContribution: new Date().toISOString(),
        status: newStatus
      });

      if (onContribute) {
        onContribute(amount);
      }

      setOpenContributeDialog(false);
      setContributeAmount(0);
      setContributePercent(10);
      setSelectedGoal(null);
      
      if (newStatus === 'funded') {
        alert('🎉 Goal funded! You can now request purchase approval!');
      }
    } catch (error) {
      console.error('Error contributing:', error);
      alert('Error adding money: ' + error.message);
    }
  };

  const handleRequestPurchase = (goal) => {
    if (goal.currentAmount < goal.targetAmount) {
      const remaining = goal.targetAmount - goal.currentAmount;
      // Use snackbar-style toast instead of alert
      console.warn(`Still need $${remaining.toFixed(2)} more`);
      return;
    }
    setConfirmRequestPurchase({ open: true, goal });
  };

  const executeRequestPurchase = async (goal) => {
    try {
      // Update goal status
      await handleUpdateGoal(goal.id, {
        status: 'pending_approval',
        requestedAt: new Date().toISOString()
      });

      // Auto-add to shopping list for parent approval
      const shoppingItem = {
        name: goal.name,
        price: goal.targetAmount,
        requestedBy: currentUser.uid.startsWith('child:') ? (goal.childName || 'Child') : 'Parent',
        status: 'pending',
        source: 'savings_goal',
        goalId: goal.id,
        store: 'amazon', // Default to Amazon, parent can toggle to Walmart
        amazonUrl: goal.amazonUrl || '',
        imageUrl: goal.imageUrl || '',
        createdAt: new Date().toISOString()
      };

      await addDoc(collection(db, 'shoppingList'), shoppingItem);
      loadGoals();
    } catch (error) {
      console.error('Error requesting purchase:', error);
    }
  };

  const handleMenuClick = (event, goal) => {
    setAnchorEl(event.currentTarget);
    setSelectedGoal(goal);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedGoal(null);
  };

  const handleOpenContribute = (goal) => {
    console.log('🎯 Opening contribute dialog for goal:', goal);
    console.log('📊 Current balance:', currentBalance);
    // Close menu first (if open) but don't clear selectedGoal yet
    setAnchorEl(null);
    // Set the goal and open dialog
    setSelectedGoal(goal);
    setContributeType(0);
    setContributeAmount(0);
    setContributePercent(10);
    setOpenContributeDialog(true);
  };

  const totalSaved = goals.reduce((sum, g) => sum + (g.currentAmount || 0), 0);
  const totalTarget = goals.reduce((sum, g) => sum + (g.targetAmount || 0), 0);
  const overallProgress = totalTarget > 0 ? (totalSaved / totalTarget) * 100 : 0;
  const completedGoals = goals.filter(g => g.status === 'funded' || g.status === 'pending_approval' || g.status === 'approved').length;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EmojiEvents color="primary" />
            My Savings Goals
          </Typography>
          <Typography variant="body2" color="text.secondary">
            ${totalSaved.toFixed(2)} saved of ${totalTarget.toFixed(2)} total goals
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => {
            setOpenDialog(true);
            setImagePreview('');
            setImageFile(null);
          }}
          size="large"
        >
          New Goal
        </Button>
      </Box>

      {/* Overall Progress */}
      {goals.length > 0 && (
        <Card sx={{ mb: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
              Overall Progress
            </Typography>
            <LinearProgress
              variant="determinate"
              value={Math.min(overallProgress, 100)}
              sx={{
                height: 12,
                borderRadius: 6,
                mb: 2,
                bgcolor: 'rgba(255,255,255,0.3)',
                '& .MuiLinearProgress-bar': {
                  bgcolor: 'white'
                }
              }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body1">
                {overallProgress.toFixed(0)}% Complete
              </Typography>
              <Typography variant="body1">
                {completedGoals} of {goals.length} Goals Reached
              </Typography>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Goals Grid */}
      {goals.length === 0 ? (
        <Card sx={{ textAlign: 'center', py: 8 }}>
          <EmojiEvents sx={{ fontSize: 80, color: 'primary.main', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            No Savings Goals Yet
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            Create your first savings goal to start working towards something amazing!
          </Typography>
          <Button
            variant="contained"
            size="large"
            startIcon={<Add />}
            onClick={() => setOpenDialog(true)}
          >
            Create Your First Goal
          </Button>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {goals.map((goal) => {
            const progress = (goal.currentAmount / goal.targetAmount) * 100;
            const remaining = goal.targetAmount - goal.currentAmount;
            const isFunded = goal.status === 'funded' || goal.currentAmount >= goal.targetAmount;
            const isPending = goal.status === 'pending_approval';

            return (
              <Grid item xs={12} sm={6} md={4} key={goal.id}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    border: isFunded ? '3px solid' : isPending ? '3px solid' : 'none',
                    borderColor: isFunded ? 'success.main' : isPending ? 'warning.main' : 'transparent',
                    position: 'relative'
                  }}
                >
                  {goal.imageUrl && (
                    <CardMedia
                      component="img"
                      height="200"
                      image={goal.imageUrl}
                      alt={goal.name}
                      sx={{ objectFit: 'cover' }}
                    />
                  )}
                  
                  {/* Status Badge */}
                  {(isFunded || isPending) && (
                    <Box sx={{ position: 'absolute', top: 16, right: 16 }}>
                      <Chip
                        icon={isPending ? <ShoppingCart /> : <Star />}
                        label={isPending ? 'Pending Approval' : 'Fully Funded!'}
                        color={isPending ? 'warning' : 'success'}
                        sx={{
                          fontWeight: 'bold',
                          boxShadow: 2
                        }}
                      />
                    </Box>
                  )}

                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Typography variant="h6" sx={{ flexGrow: 1, pr: 1 }}>
                        {goal.name}
                      </Typography>
                      <IconButton onClick={(e) => handleMenuClick(e, goal)} sx={{ minWidth: 48, minHeight: 48 }}>
                        <MoreVert />
                      </IconButton>
                    </Box>

                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          Progress
                        </Typography>
                        <Typography variant="body2" fontWeight="bold">
                          ${goal.currentAmount.toFixed(2)} / ${goal.targetAmount.toFixed(2)}
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={Math.min(progress, 100)}
                        color={isFunded ? 'success' : 'primary'}
                        sx={{ height: 10, borderRadius: 5 }}
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                        {isFunded ? '✅ Fully Funded!' : `$${remaining.toFixed(2)} to go (${progress.toFixed(0)}%)`}
                      </Typography>
                    </Box>

                    {goal.autoAllocate && !isFunded && (
                      <Chip
                        size="small"
                        label={`Auto-saving ${goal.autoAllocatePercent}%`}
                        color="primary"
                        variant="outlined"
                      />
                    )}
                  </CardContent>

                  <CardActions sx={{ p: 2, pt: 0, gap: 1, flexDirection: 'column' }}>
                    {!isPending && (
                      <Button
                        fullWidth
                        variant="outlined"
                        color="primary"
                        startIcon={<AttachMoney />}
                        onClick={() => handleOpenContribute(goal)}
                        disabled={isFunded}
                      >
                        Add Money
                      </Button>
                    )}
                    
                    {isFunded && !isPending && (
                      <Button
                        fullWidth
                        variant="contained"
                        color="success"
                        startIcon={<ShoppingCart />}
                        onClick={() => handleRequestPurchase(goal)}
                      >
                        Request Purchase
                      </Button>
                    )}
                    
                    {isPending && (
                      <Button
                        fullWidth
                        variant="contained"
                        color="warning"
                        disabled
                      >
                        ⏳ Awaiting Parent Approval
                      </Button>
                    )}
                  </CardActions>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Create Goal Dialog */}
      <Dialog open={openDialog} onClose={() => !loading && setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Savings Goal</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            {/* Amazon URL Extract */}
            <Alert severity="info">
              <strong>Tip:</strong> Paste an Amazon URL to auto-fill product details!
            </Alert>

            <TextField
              label="Amazon Product URL (Optional)"
              value={newGoal.amazonUrl}
              onChange={(e) => setNewGoal({ ...newGoal, amazonUrl: e.target.value })}
              fullWidth
              helperText="Paste Amazon link here"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Button
                      onClick={handleExtractAmazonProduct}
                      disabled={!newGoal.amazonUrl || extracting}
                      size="small"
                      variant="contained"
                    >
                      {extracting ? 'Extracting...' : 'Extract'}
                    </Button>
                  </InputAdornment>
                )
              }}
            />

            <TextField
              label="What are you saving for?"
              value={newGoal.name}
              onChange={(e) => setNewGoal({ ...newGoal, name: e.target.value })}
              fullWidth
              required
              helperText="e.g., Nintendo Switch, New Bike, LEGO Star Wars Set"
            />

            <TextField
              label="How much does it cost?"
              type="number"
              value={newGoal.targetAmount || ''}
              onChange={(e) => setNewGoal({ ...newGoal, targetAmount: e.target.value })}
              fullWidth
              required
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>
              }}
            />

            {/* Image Upload */}
            <Box sx={{ border: '2px dashed #ccc', borderRadius: 2, p: 2, textAlign: 'center' }}>
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="goal-image-upload"
                type="file"
                onChange={handleImageUpload}
              />
              <label htmlFor="goal-image-upload">
                <Button
                  component="span"
                  variant="outlined"
                  startIcon={<PhotoCamera />}
                  fullWidth
                  size="large"
                >
                  {imageFile || imagePreview ? 'Change Photo' : 'Add Photo'}
                </Button>
              </label>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Upload a photo of what you're saving for
              </Typography>

              {imagePreview && (
                <Box sx={{ mt: 2 }}>
                  <img
                    src={imagePreview}
                    alt="Preview"
                    style={{ maxWidth: '100%', maxHeight: 200, borderRadius: 8 }}
                    onError={(e) => {
                      console.log('Image failed to load:', imagePreview);
                      e.target.style.display = 'none';
                    }}
                  />
                </Box>
              )}
            </Box>

            {/* Auto-Allocate */}
            <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 2, p: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={newGoal.autoAllocate}
                    onChange={(e) => setNewGoal({ ...newGoal, autoAllocate: e.target.checked })}
                  />
                }
                label="Auto-save from my allowance"
              />
              
              {newGoal.autoAllocate && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" gutterBottom>
                    Auto-save percentage: {newGoal.autoAllocatePercent}%
                  </Typography>
                  <Slider
                    value={newGoal.autoAllocatePercent}
                    onChange={(e, value) => setNewGoal({ ...newGoal, autoAllocatePercent: value })}
                    min={5}
                    max={50}
                    step={5}
                    marks
                    valueLabelDisplay="auto"
                  />
                  <Typography variant="caption" color="text.secondary">
                    Every time you earn money, {newGoal.autoAllocatePercent}% will automatically go to this goal
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleCreateGoal}
            variant="contained"
            disabled={loading || !newGoal.name || !newGoal.targetAmount || (!imageFile && !imagePreview)}
          >
            {uploadingImage ? 'Uploading...' : loading ? 'Creating...' : 'Create Goal'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Contribute Dialog */}
      <Dialog open={openContributeDialog} onClose={() => setOpenContributeDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add Money to Goal</DialogTitle>
        <DialogContent>
          {console.log('💬 Dialog rendering - selectedGoal:', selectedGoal, 'currentBalance:', currentBalance)}
          {selectedGoal && (
            <Box sx={{ pt: 1 }}>
              <Typography variant="h6" gutterBottom>
                {selectedGoal.name}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Current: ${(selectedGoal.currentAmount || 0).toFixed(2)} / ${(selectedGoal.targetAmount || 0).toFixed(2)}
              </Typography>
              <Typography variant="body2" color="primary" gutterBottom sx={{ mb: 2 }}>
                Available Balance: ${(currentBalance || 0).toFixed(2)}
              </Typography>

              <Tabs value={contributeType} onChange={(e, v) => setContributeType(v)} sx={{ mb: 2 }}>
                <Tab label="Fixed Amount" />
                <Tab label="Percentage" />
              </Tabs>

              {contributeType === 0 ? (
                <>
                  <TextField
                    label="Amount to Add"
                    type="number"
                    value={contributeAmount || ''}
                    onChange={(e) => setContributeAmount(parseFloat(e.target.value) || 0)}
                    fullWidth
                    autoFocus
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>
                    }}
                    inputProps={{
                      max: currentBalance,
                      min: 0,
                      step: 0.01
                    }}
                  />
                  <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
                    {[5, 10, 20].map(amount => (
                      <Button
                        key={amount}
                        size="small"
                        variant="outlined"
                        onClick={() => setContributeAmount(amount)}
                      >
                        ${amount}
                      </Button>
                    ))}
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setContributeAmount(Math.min(currentBalance || 0, (selectedGoal?.targetAmount || 0) - (selectedGoal?.currentAmount || 0)))}
                    >
                      Rest
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setContributeAmount(currentBalance || 0)}
                    >
                      All
                    </Button>
                  </Box>
                </>
              ) : (
                <Box>
                  <Typography variant="body2" gutterBottom>
                    Contribute {contributePercent}% of balance = ${((currentBalance * contributePercent) / 100).toFixed(2)}
                  </Typography>
                  <Slider
                    value={contributePercent}
                    onChange={(e, value) => setContributePercent(value)}
                    min={5}
                    max={100}
                    step={5}
                    marks
                    valueLabelDisplay="auto"
                    valueLabelFormat={(value) => `${value}%`}
                  />
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenContributeDialog(false)}>Cancel</Button>
          <Button
            onClick={handleContribute}
            variant="contained"
            disabled={contributeType === 0 ? (contributeAmount <= 0 || contributeAmount > (currentBalance || 0)) : (contributePercent <= 0)}
          >
            Add Money
          </Button>
        </DialogActions>
      </Dialog>

      {/* Goal Menu */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleCloseMenu}>
        <MenuItem onClick={() => handleOpenContribute(selectedGoal)}>
          <AttachMoney sx={{ mr: 1 }} /> Add Money
        </MenuItem>
        {selectedGoal?.amazonUrl && (
          <MenuItem onClick={() => {
            window.open(selectedGoal.amazonUrl, '_blank');
            handleCloseMenu();
          }}>
            <Launch sx={{ mr: 1 }} /> View on Amazon
          </MenuItem>
        )}
        <MenuItem onClick={() => handleDeleteGoal(selectedGoal?.id)} sx={{ color: 'error.main' }}>
          <Delete sx={{ mr: 1 }} /> Delete Goal
        </MenuItem>
      </Menu>

      {/* Confirm Delete Goal Dialog */}
      <Dialog open={confirmDeleteGoal.open} onClose={() => setConfirmDeleteGoal({ open: false, goalId: null })} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Goal?</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this savings goal? This cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteGoal({ open: false, goalId: null })} sx={{ minHeight: 48 }}>Cancel</Button>
          <Button variant="contained" color="error" sx={{ minHeight: 48 }} onClick={() => {
            const { goalId } = confirmDeleteGoal;
            setConfirmDeleteGoal({ open: false, goalId: null });
            executeDeleteGoal(goalId);
          }}>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Request Purchase Dialog */}
      <Dialog open={confirmRequestPurchase.open} onClose={() => setConfirmRequestPurchase({ open: false, goal: null })} maxWidth="xs" fullWidth>
        <DialogTitle>Request Purchase?</DialogTitle>
        <DialogContent>
          <Typography>
            Request approval to purchase "{confirmRequestPurchase.goal?.name}" for ${confirmRequestPurchase.goal?.targetAmount?.toFixed(2)}?
            This will add the item to your parent's shopping list.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRequestPurchase({ open: false, goal: null })} sx={{ minHeight: 48 }}>Cancel</Button>
          <Button variant="contained" color="primary" sx={{ minHeight: 48 }} onClick={() => {
            const { goal } = confirmRequestPurchase;
            setConfirmRequestPurchase({ open: false, goal: null });
            executeRequestPurchase(goal);
          }}>Request</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default SavingsGoalsEnhanced;

